import { useQuery } from "@apollo/client";
import { PERSON_BY_ID_QUERY } from "../../../../api/person/queries";
import {
  defaultPersonEfteruddannelseColumns,
  defaultPersonForsikringColumns,
  defaultPersonKlienterklaeringColumns,
  defaultPersonTilsynerklaeringColumns,
} from "../../../../config/person";
import useActiveColumns from "../../../../hooks/useActiveColumns";
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from "../../../accordion/Accordion";
import BasisCards from "./Tilsyn/BasisCards";
import Efteruddannelse from "./Tilsyn/Efteruddannelse";
import Forsikringer from "./Tilsyn/Forsikringer";
import GennemfoerteTilsyn from "./Tilsyn/GennemfoerteTilsyn";
import Klientkontoerklaering from "./Tilsyn/Klientkontoerklaering";
import TilsynsErklaering from "./Tilsyn/TilsynsErklaering";
import { getIndberetningFrist, getLektionerOverfoert } from "./Tilsyn/util";
import { PersonDto } from "../../../../interfaces/schema";
import usePersonChecks from "../../../../hooks/errorHandling/usePersonChecks";
import { useState } from "react";
import { transformQueryDataPerioder } from "../../../../util/apollo";

export default function Tilsyn({ personId }: { personId: string }) {
  const [personEfteruddannelseColumns, setPersonEfteruddannelseActiveColumns] =
    useActiveColumns(
      "person-personefteruddannelse-active-columns",
      defaultPersonEfteruddannelseColumns,
    );
  const [
    personKlienterklaeringColumns,
    setPersonKlienterklaeringActiveColumns,
  ] = useActiveColumns(
    "person-personklienterklaering-active-columns",
    defaultPersonKlienterklaeringColumns,
  );
  const [personForsikringColumns, setPersonForsikringActiveColumns] =
    useActiveColumns(
      "person-personforsikring-active-columns",
      defaultPersonForsikringColumns,
    );
  const [
    personTilsynerklaeringColumns,
    setPersonTilsynerklaeringActiveColumns,
  ] = useActiveColumns(
    "person-persontilsynerklaering-active-columns",
    defaultPersonTilsynerklaeringColumns,
  );

  const [data, setData] = useState<
    | {
        person: PersonDto;
      }
    | undefined
  >(undefined);
  const { loading, error, refetch, networkStatus } = useQuery<{
    person: PersonDto;
  }>(
    PERSON_BY_ID_QUERY("personByIdTilsyn", {
      personForsikring: true,
      personTilsynserklaering: true,
      personTilsyn: true,
      personEfteruddannelse: true,
      personKlienterklaering: true,
    }),
    {
      notifyOnNetworkStatusChange: true,
      variables: { id: personId },
      onCompleted: (d) => {
        const transformed = transformQueryDataPerioder(d);
        setData(transformed);
      },
    },
  );
  const { checkResult, person } = usePersonChecks(
    loading,
    error,
    data,
    networkStatus,
  );
  if (checkResult) return checkResult;
  const { personTilsynserklaering, personTilsyn, personKlienterklaering } =
    person;

  // Extract and add modtaget dato to personEfteruddannelse
  const personEfterUddannelse = person.personEfteruddannelse?.map((eu) => {
    const sortedIndberetning = [...eu.personEfteruddannelseIndberetning].sort(
      (a, b) => {
        const aDate = a.registreringsDato || new Date(0);
        const bDate = b.registreringsDato || new Date(0);
        return bDate > aDate ? 1 : -1;
      },
    );
    const modtagetDato = sortedIndberetning.length
      ? sortedIndberetning[0].registreringsDato
      : "9999-12-12";
    const lektionerOverfoert = getLektionerOverfoert(eu);
    const indberetningFrist = getIndberetningFrist(eu);

    return { ...eu, modtagetDato, lektionerOverfoert, indberetningFrist };
  });

  return (
    <div className="container">
      <BasisCards person={person} />
      <AccordionRoot localstorageKey="person-id-tilsyn-accordion-value">
        <AccordionItem
          className="c-card c-card--accordion"
          value="efteruddannelse"
          isEmpty={!personEfterUddannelse?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Efteruddannelse</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Efteruddannelse
              personId={personId}
              invalidateData={refetch}
              columns={personEfteruddannelseColumns}
              person={person}
              setActiveColumns={setPersonEfteruddannelseActiveColumns}
              defaultColumns={defaultPersonEfteruddannelseColumns}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="klientkontoerklaering"
          isEmpty={!personKlienterklaering?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Klientkontoerklæring</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Klientkontoerklaering
              personId={personId}
              invalidateData={refetch}
              columns={personKlienterklaeringColumns}
              setActiveColumns={setPersonKlienterklaeringActiveColumns}
              personKlientErklaering={personKlienterklaering ?? []}
              defaultColumns={defaultPersonKlienterklaeringColumns}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="tilsynserklaering"
          isEmpty={!personTilsynserklaering?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Tilsynserklæring</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <TilsynsErklaering
              personId={personId}
              invalidateData={refetch}
              columns={personTilsynerklaeringColumns}
              personTilsynsErklaering={personTilsynserklaering ?? []}
              setActiveColumns={setPersonTilsynerklaeringActiveColumns}
              defaultColumns={defaultPersonTilsynerklaeringColumns}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="gennemfoertetilsyn"
          isEmpty={!personTilsyn?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Gennemførte tilsyn</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <GennemfoerteTilsyn
              person={person}
              personTilsyn={person.personTilsyn ?? []}
              invalidateData={refetch}
            />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem
          className="c-card c-card--accordion"
          value="forsikringer"
          isEmpty={!person.personForsikring?.length}
        >
          <AccordionHeader>
            <AccordionTrigger>
              <span>Forsikringer</span>
            </AccordionTrigger>
          </AccordionHeader>
          <AccordionContent>
            <Forsikringer
              personId={personId}
              columns={personForsikringColumns}
              setActiveColumns={setPersonForsikringActiveColumns}
              defaultColumns={defaultPersonForsikringColumns}
              invalidateData={refetch}
              kundeNr={person.kundeNr}
              personForsikring={person.personForsikring ?? []}
            />
          </AccordionContent>
        </AccordionItem>
      </AccordionRoot>
    </div>
  );
}
